import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Button,
  Typography
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {
  BackdropCircular,
  ConfirmDialog,
  BuyExtraPromosModal
} from 'components';
import {
  getTierSubscriptionPlansApi,
  updateCurrentUserSubscriptionPlanApi
} from 'api';


const cardLabelColors = ['#5BCDDA', '#2D9CDB', '#2F80ED'];

const useStyles = makeStyles(() => ({
  cardLabel: {
    position: 'absolute',
    left: '-6px',
    right: '-6px',
    textTransform: 'uppercase',
    color: '#fff',
    lineHeight: '24px',
    fontWeight: '500',
    fontSize: '16px',
    padding: '15px 0',
    borderRadius: '6px'
  }
}));

export default function SubscriptionPlans() {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const {
    loadingProfile,
    profile,
    loadingUpdateCurrentUserPlan,
    subscriptionTiers,
    buyingSubscriptionAddon
  } = useSelector((state) => state.user);

  const store = profile?.stores?.[0];
  const userTier = subscriptionTiers.find((tire) => tire._id === store?.subscriptionTier);

  const loading = loadingProfile || loadingUpdateCurrentUserPlan || buyingSubscriptionAddon;

  const activePlanId = store?.subscriptionId;

  useEffect(() => {
    getTierSubscriptionPlansApi({dispatch});
  }, []);

  const handleChangeSubscriptionPlan = () => {
    updateCurrentUserSubscriptionPlanApi({dispatch, userId: profile._id, storeId: profile?.stores[0]._id, tierId: userTier._id,  planId: selectedPlanId});
    setOpenConfirmDialog(false);
  };

  return (
    <div className="flex-grow flex-column relative h-full scroll-y">
      <div className="m-10">
        <Card className="p-8">
          <div className="flex flex-center text-center flex-wrap">
            {userTier ?
              userTier.plans.map((plan, idx) => {
                const {_id, title, price, priceDueToday, savings, promoSkusLimit, promosPerMonthLimit } = plan;
                const isSelected = activePlanId === _id;
                const { maxPromoSkus, maxActivePromos } = store?.limit || {};
                return (
                  <Card elevation={0} key={_id} className="p-8 m-6 bg-default w-full max-w-280 relative overflow-visible text-dark-gray">
                    <div className={classes.cardLabel} style={{backgroundColor: `${cardLabelColors[idx]}`}}>{title}</div>
                    <div className="mt-16 text-28 font-medium">{'$'}{(Number(price) - ((Number(price) * savings / 100))).toFixed(0)}</div>
                    {savings ?
                      <>
                        <div className="uppercase text-medium-gray font-medium">Monthly</div>
                        <div className="my-5">
                          <div className="text-20 font-semibold">{savings}%</div>
                          <div className="text-14 text-medium-gray font-normal">Savings</div>
                        </div>
                      </>
                      :
                      <div className="min-h-100 mb-2" />
                    }
                    <div className="text-20 text-dark-gray font-semibold">
                      {'$'}{priceDueToday.toLocaleString('en-US', {minimumFractionDigits: 0})}
                    </div>
                    <div className="text-14 text-medium-gray font-normal mb-2">Due Today</div>
                    <div className="text-12 text-medium-gray text-left font-normal">
                      * SKUS in active promos - {promoSkusLimit}
                      {maxPromoSkus - promoSkusLimit > 0 && ` (+${maxPromoSkus - promoSkusLimit})`}
                    </div>
                    <div className="text-12 text-medium-gray text-left font-normal">
                      * Concurrent active promos - {promosPerMonthLimit}
                      {maxActivePromos - promosPerMonthLimit > 0 && ` (+${maxActivePromos - promosPerMonthLimit})`}
                    </div>
                    <Button
                      disabled={isSelected}
                      color="primary"
                      variant="contained"
                      className="mt-8 px-6 border-radius-6"
                      onClick={() => {
                        setOpenConfirmDialog(true);
                        setSelectedPlanId(_id);
                      }}
                    >
                      {isSelected ? 'Selected' : 'Select'}
                    </Button>
                  </Card>
                );
              })
              :
              <div>
                <Typography variant="h4" className="font-semibold pb-4">You must pick a billing frequency before your store goes live</Typography>
                {loading ? null : <p className="text-muted text-left">You are not assigned to any Subscription Tier Plan. Please contact to our support team!</p>}
              </div>
            }
          </div>
          {userTier?._id && activePlanId &&
          <Button
            color="primary"
            variant="contained"
            className="mt-8 px-6 border-radius-6"
            onClick={() => setShowPurchaseModal(true)}
          >
            Buy extra promo
          </Button>}
          <BuyExtraPromosModal showPurchaseModal={showPurchaseModal} setShowPurchaseModal={setShowPurchaseModal} />
          <ConfirmDialog
            title="Do you want to change your plan?"
            open={openConfirmDialog}
            setOpen={setOpenConfirmDialog}
            onConfirm={() => handleChangeSubscriptionPlan()}
          >
            <p>Are you sure you want to change your current billing plan? </p>
            <p><strong>Note: </strong> This action will take affect with a next billing cycle.</p>
          </ConfirmDialog>
          <BackdropCircular loading={loading} />
        </Card>
      </div>
    </div>
  );
}
